import { Box, Button, useToast } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { ApiMessageStack, Message, TableV2 } from "components";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { IntegrationEntitySyncStatusEnum } from "enums/integration-entity-sync-status.enum";
import { AlertStatusEnum } from "enums/alert-status.enum";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { keyBy } from "lodash";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { syncReapitLeadSourceTableColumnDef } from "./syncReapitLeadSourceTableColumnDef";
import { TReapitSyncLeadSourceExtendedData } from "types/reapit.type";
import { IntegrationConfiguratorLayout } from "../../IntegrationConfiguratorLayout";
import { SyncStatusFilter } from "../../SyncStatusFilter";

const DEFAULT_PAGE_SIZE = 10;

interface IProps {
  accountStore: UserAccountStore;
  selectedOfficeGroupId?: number;
}

export const ReapitIntegrationsLeadSourcesConfigurator: FC<IProps> = observer(
  ({ accountStore, selectedOfficeGroupId }) => {
    const toast = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newRelations, setNewRelations] = useState<
      Record<number, TReapitSyncLeadSourceExtendedData>
    >({});

    const accountIntegrationsStore = accountStore.accountIntegrationsStore;
    const accountIntegrationsReapitStore =
      accountIntegrationsStore.accountIntegrationReapitStore;
    const accountIntegrationReapitLeadSourcesStore =
      accountIntegrationsReapitStore.accountIntegrationReapitLeadSourcesStore;

    const {
      filteredReapitSyncLeadSourceData,
      reapitSyncLeadSourceData,
      setSyncStatusFilter,
      getSyncStatusFilter: syncStatusFilter,
    } = accountIntegrationReapitLeadSourcesStore;

    const updateSyncPair = useCallback(
      (syncPair: TReapitSyncLeadSourceExtendedData) => {
        setNewRelations({
          ...newRelations,
          [syncPair.leadSourceId]: syncPair,
        });
      },
      [setNewRelations, newRelations]
    );

    useEffect(() => {
      const suggestedArray = reapitSyncLeadSourceData.filter(
        syncData =>
          syncData.syncStatus === IntegrationEntitySyncStatusEnum.SUGGESTED_SYNC
      );
      const suggestedMap = keyBy<TReapitSyncLeadSourceExtendedData>(
        suggestedArray,
        syncData => syncData.leadSourceId
      );
      setNewRelations({
        ...suggestedMap,
      });
    }, [reapitSyncLeadSourceData]);

    const columnsDef = useMemo(() => {
      return syncReapitLeadSourceTableColumnDef(accountStore, updateSyncPair);
    }, [accountStore, updateSyncPair]);

    const dataSource = useMemo(() => {
      return filteredReapitSyncLeadSourceData.map(syncData => {
        if (!!newRelations[syncData.leadSourceId])
          return newRelations[syncData.leadSourceId];

        return syncData;
      });
    }, [filteredReapitSyncLeadSourceData, newRelations]);

    const submitSyncPairs = useCallback(async () => {
      try {
        setIsSubmitting(true);
        await accountIntegrationReapitLeadSourcesStore.updateAccountReapitSyncedLeadSources(
          Object.values(newRelations),
          selectedOfficeGroupId
        );
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: (
            <ApiMessageStack
              messageStack={
                "Reapit integration lead sources configuration updated."
              }
            />
          ),
        });
      } catch (error) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={error.message} />,
        });
      } finally {
        setIsSubmitting(false);
      }
    }, [
      newRelations,
      accountIntegrationReapitLeadSourcesStore,
      toast,
      selectedOfficeGroupId,
    ]);

    const banner = useMemo(() => {
      const notSyncedArray = reapitSyncLeadSourceData.filter(
        syncData =>
          syncData.syncStatus !== IntegrationEntitySyncStatusEnum.SYNCED
      );

      if (notSyncedArray.length > 0) {
        return (
          <Message status={AlertStatusEnum.WARNING}>
            <Box>
              {`${notSyncedArray.length} of your lead sources are not syncing to LeadPro`}
            </Box>
          </Message>
        );
      }

      return null;
    }, [reapitSyncLeadSourceData]);

    const handleFilterChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setSyncStatusFilter(event.target.checked);
      },
      [setSyncStatusFilter]
    );

    return (
      <IntegrationConfiguratorLayout>
        <Box fontSize={"xl"}>Lead source mappings</Box>
        {banner}
        <TableV2<TReapitSyncLeadSourceExtendedData>
          globalFilterInputPlaceholder={"Search sources by name"}
          additionalActions={
            <SyncStatusFilter
              isChecked={syncStatusFilter}
              onChange={handleFilterChange}
            />
          }
          dataSource={dataSource}
          columns={columnsDef}
          pageSize={DEFAULT_PAGE_SIZE}
        />
        <Box width={"100%"} textAlign={"right"}>
          <Button
            colorScheme={"blue"}
            onClick={submitSyncPairs}
            isLoading={isSubmitting}
          >
            Update
          </Button>
        </Box>
      </IntegrationConfiguratorLayout>
    );
  }
);
